import {
  CHAT_LIMITS,
  CHAT_REPORT_REASON_NAMES,
  CHAT_REPORT_REASONS,
  type ChatMessageView,
  type ChatReportReason,
} from '@mogzo/shared';
import { Check, Info, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import { reportMessage } from '../../lib/chat';
import { Avatar } from '../community/Avatar';
import { Alert, buttonClass } from '../ui';
import { Say } from './parts';

/**
 * Reporting a message (M38): why, and whether to block them too. Staff see it with the 20
 * messages before it; the player reported isn't told who.
 */
export function ReportDialog({
  message,
  onClose,
}: {
  message: ChatMessageView;
  onClose: (blocked: boolean) => void;
}) {
  const [reason, setReason] = useState<ChatReportReason | null>(null);
  const [note, setNote] = useState('');
  const [block, setBlock] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const titleId = useId();
  const box = useRef<HTMLElement>(null);
  const who = message.from.username;

  useEffect(() => {
    box.current?.focus();
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose(sent && block);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, sent, block]);

  async function send() {
    if (!reason) return;
    setBusy(true);
    setError(null);
    try {
      await reportMessage(message.id, {
        reason,
        note: reason === 'other' && note.trim() ? note.trim() : undefined,
        block,
      });
      setSent(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not send it. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/45 sm:items-center sm:p-4">
      <section
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="max-h-[92dvh] w-full max-w-[520px] overflow-y-auto rounded-t-[22px] bg-card px-6 pt-5 pb-6 shadow-[0_16px_48px_rgba(27,21,47,0.2)] focus:outline-none sm:rounded-[22px]"
      >
        {sent ? (
          <div className="py-3 text-center">
            <span className="mx-auto grid size-14 place-items-center rounded-full bg-[#e8f5ee] text-[#157244]">
              <Check size={24} strokeWidth={2} aria-hidden="true" />
            </span>
            <h2 id={titleId} className="mt-3 text-[22px] font-bold">
              Report sent
            </h2>
            <p className="mt-1.5 text-muted">
              Staff will look at it.{' '}
              {block
                ? `You’ve blocked ${who}: they can’t message you.`
                : `You can still block ${who} from the chat’s menu.`}
            </p>
            <button
              type="button"
              onClick={() => onClose(block)}
              className={`${buttonClass('primary', false)} mt-5`}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <h2 id={titleId} className="text-[22px] leading-7 font-bold">
                  Report this message
                </h2>
                <p className="mt-1 text-muted">
                  MOGZO staff see it with the 20 messages before it. {who} isn’t told who reported
                  it.
                </p>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => onClose(false)}
                className="-mt-1.5 -mr-2 flex size-10 items-center justify-center rounded-full hover:bg-field"
              >
                <X size={20} strokeWidth={1.9} aria-hidden="true" />
              </button>
            </div>
            <div className="mt-3.5 flex items-end gap-2.5">
              <Avatar name={who} size="xs" />
              <p className="rounded-2xl bg-field px-3.5 py-2.5 text-[15px] leading-[21px] break-words whitespace-pre-wrap">
                {message.text}
              </p>
            </div>
            <fieldset className="mt-4.5 border-0 p-0">
              <legend className="mb-2.5 font-semibold">What’s wrong with it?</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {CHAT_REPORT_REASONS.map((value) => (
                  <label
                    key={value}
                    className={`flex cursor-pointer items-center gap-3 rounded-[14px] border px-3.5 py-3 ${reason === value ? 'border-ink shadow-[0_0_0_3px_var(--color-cream)]' : 'border-line'}`}
                  >
                    <input
                      type="radio"
                      name="reason"
                      checked={reason === value}
                      onChange={() => setReason(value)}
                      className="size-5 accent-ink"
                    />
                    <span className="font-medium">{CHAT_REPORT_REASON_NAMES[value]}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            {reason === 'self_harm' && (
              <div className="mt-3">
                <Say tone="info" icon={Info}>
                  Staff see this first. If someone is in danger right now, call your local emergency
                  number.
                </Say>
              </div>
            )}
            {reason === 'other' && (
              <label className="mt-3 block">
                <span className="mb-1.5 block font-semibold">Tell staff what happened</span>
                <textarea
                  value={note}
                  maxLength={CHAT_LIMITS.reportNote}
                  onChange={(event) => setNote(event.target.value)}
                  rows={3}
                  className="w-full rounded-[14px] border border-subtle px-3.5 py-2.5 text-[15px] focus:border-ink focus:outline-none"
                />
              </label>
            )}
            <label className="mt-4 flex items-center gap-2.5">
              <input
                type="checkbox"
                checked={block}
                onChange={(event) => setBlock(event.target.checked)}
                className="size-5 accent-ink"
              />
              Block {who} too
            </label>
            {error && (
              <div className="mt-3">
                <Alert>{error}</Alert>
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                className={buttonClass('secondary', false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!reason || busy}
                onClick={send}
                className={buttonClass('primary', false)}
              >
                {busy ? 'Sending…' : 'Send report'}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
